import { ButtonPrimary } from '@modules/common/components/form/buttons/button-primary';
import { AjustedVariant } from '@modules/products/templates';
import { ErrorMessage, FieldInputProps, FormikProps } from 'formik';
import { handleAmountSelection } from '../handles/handle-amount-selection';
import ReactInputMask from 'react-input-mask';
import { useEffect, useState } from 'react';
import { toaster } from '@lib/util/toaster';
import { featureFlags } from '@lib/constants/network-constants';
import { getFeatureFlagState } from '@lib/data';
import { DEFAULT_MIN_PRICE } from '@lib/constants';

type AmoutItemsProps = {
  variants: AjustedVariant[];
  customVariants: AjustedVariant[];
  field: FieldInputProps<AjustedVariant>;
  form: FormikProps<any>;
  name: string;
};

export const ButtonsAmount = (props: AmoutItemsProps) => {
  const { variants, customVariants, field, form, name } = props;
  const [amount, setAmount] = useState('');
  const [hasEnterAmount, setHasEnterAmount] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [minPrice, setMinPrice] = useState(DEFAULT_MIN_PRICE);

  const itemTitle = {
    id: 'enter',
    title: 'Enter Amount',
    prices: 0,
    fee: 495,
  };

  const getCustomVariant = (value: number) => {
    const centValue = value * 100;
    return customVariants.find((item) => item.prices === centValue);
  };

  const handleChangeAmount = (value: any) => {
    setAmount(value?.target?.value?.replace('$', ''));
  };

  const handleCheckAmountChange = async () => {
    if (!amount) {
      // Reset if empty
      if (field.value.title === itemTitle.title) {
        // Only hide input if we're still on the custom button
        setShowInput(false);
      }
      return;
    }

    if (Number(amount) > 249) {
      toaster.error('Maximum custom amount is $249');
      return;
    }

    if (Number(amount) < minPrice) {
      toaster.error(`Minimum custom amount is ${minPrice}`);
      return;
    }

    const res: AjustedVariant | undefined = getCustomVariant(Number(amount));
    if (res) {
      handleAmountSelection({
        item: {
          ...itemTitle,
          id: res.id,
          prices: res.prices,
          fee: res.fee,
        },
        field,
        form,
      });
    }

    // Keep showing input after valid submission
  };

  const handleEnterAmountClick = () => {
    // Always enter editing mode on click
    setShowInput(true);

    // Select the custom amount button in formik
    handleAmountSelection({
      item: itemTitle,
      field,
      form,
    });

    // Use requestAnimationFrame to focus the input after the DOM updates
    requestAnimationFrame(() => {
      const inputElement = document.querySelector('.input-amount') as HTMLInputElement;
      if (inputElement) {
        inputElement.focus();
      }
    });
  };

  const getMinPrice = async () => {
    const price = Math.min(...variants.map((item) => item.prices));
    setMinPrice(price ? price / 100 : DEFAULT_MIN_PRICE); // Default to $10 if no variants
  };

  // Reset input display when selecting a non-custom button
  useEffect(() => {
    if (field.value.title !== itemTitle.title) {
      setShowInput(false);
    }
  }, [field.value.title]);

  useEffect(() => {
    const getFeatureFlag = async () => {
      if (await getFeatureFlagState(featureFlags.VARIABLE_CARD_LOAD)) {
        setHasEnterAmount(true);
      }
    };
    getFeatureFlag();

    getMinPrice();
  }, []);

  return (
    <div>
      <div className="card__amount__items gap-1.5 flex flex-row flex-wrap">
        {variants.map((item, _index) => (
          <div key={item.id}>
            <ButtonPrimary
              type="button"
              onClick={() => handleAmountSelection({ item, field, form })}
              size="medium"
              active={field.value.title === item.title}
              className="!py-1 !px-2 !text-sm hasGraybtn"
            >
              {item.title}
            </ButtonPrimary>
          </div>
        ))}

        {hasEnterAmount && (
          <ButtonPrimary
            type="button"
            onClick={handleEnterAmountClick}
            size="medium"
            active={field.value.title === itemTitle.title}
            className="!py-1 !px-2 !text-sm hasGraybtn"
          >
            {showInput || field.value.title === itemTitle.title ? (
              <ReactInputMask
                mask="$999"
                maskChar={null}
                placeholder="Enter Amount"
                className="input-amount bg-transparent focus-within:outline-none w-[80px] text-center"
                onChange={handleChangeAmount}
                onBlur={handleCheckAmountChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                    e.preventDefault();
                  }
                }}
                autoFocus
              />
            ) : (
              'Enter Amount'
            )}
          </ButtonPrimary>
        )}
      </div>
      <ErrorMessage name={name} component="p" className="text-red-500 text-xs mt-1 text-left" />
    </div>
  );
};
